
<template>
  <div class="container">
    <!-- Search -->
    <div class="filter-container">
      <div class="filter-item search-item">
        <label for="trustSearch">Search</label>
        <input
          id="trustSearch"
          type="text"
          v-model="trustSearch"
          class="search-input"
          placeholder="Search by dealer ID, remarks or created by..."
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-spinner">
      <i class="fa fa-spinner fa-spin"></i> Loading pending trusts...
    </div>

    <!-- Table -->
    <div
      v-if="!loading && filteredTrusts.length > 0"
      class="table-container"
    >
      <table>
        <thead>
          <tr>
            <th>Trust ID</th>
            <th>Dealer ID</th>
            <th>Remarks</th>
            <th>Items</th>
            <th>Documents</th>
            <th>Created By</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="trust in filteredTrusts" :key="trust.id">
            <td>{{ trust.id }}</td>
            <td>{{ trust.dealerId }}</td>
            <td class="remarks-cell">{{ trust.remarks || "N/A" }}</td>
            <td>{{ trust.itemsCount }}</td>
            <td>{{ trust.documentsCount }}</td>
            <td>{{ trust.createdBy || "N/A" }}</td>
            <td>{{ formatDateTime(trust.createdAt) }}</td>
            <td class="dealer-actions">
                <button
                    type="button"
                    class="icon-button"
                    title="Review Trust"
                    @click="reviewTrust(trust.id)"
                >
                    <i class="fa fa-eye"></i>
                </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- No pending records -->
    <div
      v-if="!loading && filteredTrusts.length === 0"
      class="not-found-widget"
    >
      <img
        src="@/assets/vectors/data_not_found.png"
        alt="No Pending Trusts"
        class="error-image"
      />
      <p class="error-message">
        {{ trustSearch ? "No matching trust records found." : "No Pending Trusts!" }}
      </p>
    </div>
  </div>
</template>

<script>
import {
  fetchPendingTrustList,
  updateTrustApprovalStatus,
} from "@/services/dataService";

export default {
  name: "TrustApprovalList",

  data() {
    return {
      token: localStorage.getItem("token_set"),
      trustSearch: "",
      trusts: [],
      loading: false,
      updatingId: null,
    };
  },

  computed: {
    filteredTrusts() {
      const search = this.trustSearch.trim().toLowerCase();

      if (!search) {
        return this.trusts;
      }

      return this.trusts.filter((trust) =>
        String(trust.id ?? "").toLowerCase().includes(search) ||
        String(trust.dealerId ?? "").toLowerCase().includes(search) ||
        String(trust.remarks ?? "").toLowerCase().includes(search) ||
        String(trust.createdBy ?? "").toLowerCase().includes(search)
      );
    },
  },

  mounted() {
    this.fetchTrusts();
  },

  activated() {
    this.fetchTrusts();
  },

  methods: {
    async fetchTrusts() {
      try {
        this.loading = true;

        const response = await fetchPendingTrustList(this.token);

        if (response.data.status === "success") {
          this.trusts = (response.data.data || []).map((record) => ({
            id: record.id,
            dealerId: record.dealer_id,
            remarks: record.remarks,
            createdBy: record.created_by_name,
            createdAt: record.created_at,
            itemsCount: Number(record.items_count || 0),
            documentsCount: Number(record.documents_count || 0),
          }));
        } else {
          this.toast?.error?.(
            response.data.message || "Failed to fetch pending trusts."
          );
        }
      } catch (error) {
        console.error("Failed to fetch pending trusts.", error);
        this.toast?.error?.("Failed to fetch pending trusts.");
      } finally {
        this.loading = false;
      }
    },

    reviewTrust(id) {
        this.$router.push({
            path: `/review-trust/${id}`,
        });
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";

      const date = new Date(dateTime);

      if (isNaN(date.getTime())) return "Invalid Date";

      const formattedDate = date.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      return `${formattedDate} | ${formattedTime}`;
    },
  },
};
</script>

<style scoped>
.container {
  background-color: #fff;
  border-radius: 8px;
  padding: 10px;
  width: 100%;
  box-sizing: border-box;
}

.filter-container {
  display: flex;
  width: 100%;
  margin-bottom: 10px;
}

.filter-item {
  display: flex;
  flex-direction: column;
}

.search-item {
  width: 340px;
  max-width: 100%;
}

label {
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

input[type="text"] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 15px;
  width: 100%;
  height: 40px;
  box-sizing: border-box;
}

input[type="text"]:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.3);
}

.loading-spinner {
  text-align: center;
  margin-top: 20px;
  font-size: 18px;
  color: #007bff;
}

.table-container {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  margin: 10px 0;
}

th {
  text-align: left;
  background-color: #f4f4f4;
  border-bottom: 2px solid #ddd;
  padding: 10px;
  white-space: nowrap;
}

td {
  padding: 10px;
  border-bottom: 1px solid #eee;
  text-align: left;
  vertical-align: middle;
}

.remarks-cell {
  min-width: 150px;
  max-width: 250px;
  white-space: normal;
  overflow-wrap: anywhere;
}

.actions-cell {
  white-space: nowrap;
}

.action-button {
  padding: 7px 10px;
  margin: 2px;
  border: none;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
}

.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.approve-button {
  background-color: #198754;
}

.approve-button:hover:not(:disabled) {
  background-color: #146c43;
}

.reject-button {
  background-color: #dc3545;
}

.reject-button:hover:not(:disabled) {
  background-color: #b02a37;
}

.action-spinner {
  margin-left: 5px;
  color: #007bff;
}

.not-found-widget {
  max-width: 500px;
  margin: 20px auto;
  text-align: center;
}

.not-found-widget img {
  width: 250px;
  max-width: 100%;
}

.error-message {
  color: grey;
  font-size: 18px;
  font-weight: bold;
  margin-top: -15px;
}

.dealer-actions {
  text-align: center;
}

.icon-button {
  margin: 0;
  font-size: 15px;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}

@media (max-width: 768px) {
  .search-item {
    width: 100%;
  }
}
</style>
